import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adaptiveLearningService, WeakTopic, ReviewTopic } from '../services/adaptiveLearningService';

const RecommendationsPanel: React.FC = () => {
    const navigate = useNavigate();
    const [weakTopics, setWeakTopics] = useState<WeakTopic[]>([]);
    const [reviewTopics, setReviewTopics] = useState<ReviewTopic[]>([]);
    const [inactiveSubjects, setInactiveSubjects] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRecommendations = async () => {
            setLoading(true);
            const data = await adaptiveLearningService.getRecommendations();
            setWeakTopics(data.weakTopics);
            setReviewTopics(data.reviewTopics);
            setInactiveSubjects(data.inactiveSubjects);
            setLoading(false);
        };

        fetchRecommendations();
    }, []);

    const handlePractice = (subject: string, topic: string) => {
        navigate(`/quiz?subject=${encodeURIComponent(subject)}&topic=${encodeURIComponent(topic)}&difficulty=Medium&timed=false&grade=4&state=TX`);
    };

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
                <div className="flex items-center justify-center h-32 text-slate-400">
                    <i className="fas fa-spinner fa-spin text-2xl mr-3"></i> Loading recommendations...
                </div>
            </div>
        );
    }

    const hasRecommendations = weakTopics.length > 0 || reviewTopics.length > 0 || inactiveSubjects.length > 0;

    if (!hasRecommendations) {
        return (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-3xl shadow-lg border border-green-200">
                <div className="text-center">
                    <i className="fas fa-trophy text-5xl text-green-500 mb-3"></i>
                    <h3 className="text-xl font-bold text-green-900 mb-2">Great Job!</h3>
                    <p className="text-green-700">You're doing well across all subjects. Keep up the excellent work!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Weak Topics - Need Practice */}
            {weakTopics.length > 0 && (
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-red-100">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                        <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
                        Topics That Need Practice
                    </h3>
                    <div className="space-y-3">
                        {weakTopics.map((topic, idx) => (
                            <div
                                key={idx}
                                className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200 hover:shadow-md transition-shadow"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-slate-800">{topic.topic}</span>
                                        <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded-full font-semibold">
                                            {topic.subject}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-slate-600">
                                        <span>
                                            <i className="fas fa-chart-line mr-1 text-red-500"></i>
                                            Avg: {Math.round(topic.avg_score)}%
                                        </span>
                                        <span>
                                            <i className="fas fa-redo mr-1"></i>
                                            {topic.attempts} {topic.attempts === 1 ? 'attempt' : 'attempts'}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handlePractice(topic.subject, topic.topic)}
                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl font-semibold transition-colors flex items-center gap-2"
                                >
                                    <i className="fas fa-dumbbell"></i>
                                    Practice
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Review Topics - Spaced Repetition */}
            {reviewTopics.length > 0 && (
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-yellow-100">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                        <i className="fas fa-clock text-yellow-500 mr-2"></i>
                        Time for Review
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                        You did well on these topics before. Let's keep them fresh!
                    </p>
                    <div className="space-y-3">
                        {reviewTopics.map((topic, idx) => (
                            <div
                                key={idx}
                                className="flex items-center justify-between p-4 bg-yellow-50 rounded-xl border border-yellow-200 hover:shadow-md transition-shadow"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-slate-800">{topic.topic}</span>
                                        <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full font-semibold">
                                            {topic.subject}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-slate-600">
                                        <span>
                                            <i className="fas fa-star mr-1 text-yellow-500"></i>
                                            Previous: {Math.round(topic.avg_score)}%
                                        </span>
                                        <span>
                                            <i className="fas fa-calendar mr-1"></i>
                                            {Math.round(topic.days_since_last)} days ago
                                        </span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handlePractice(topic.subject, topic.topic)}
                                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-xl font-semibold transition-colors flex items-center gap-2"
                                >
                                    <i className="fas fa-sync"></i>
                                    Review
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Inactive Subjects */}
            {inactiveSubjects.length > 0 && (
                <div className="bg-white p-6 rounded-3xl shadow-lg border border-blue-100">
                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                        <i className="fas fa-lightbulb text-blue-500 mr-2"></i>
                        Try Something New
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">
                        You haven't practiced these subjects recently. Give them a try!
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {inactiveSubjects.map((subject, idx) => (
                            <button
                                key={idx}
                                onClick={() => handlePractice(subject, '')}
                                className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-4 py-2 rounded-xl font-semibold transition-colors"
                            >
                                {subject}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecommendationsPanel;
